import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import { FACEBOOK_URL, INSTAGRAM_URL } from "../../utils/socialLinks";
import { ContentPageItem } from "../../api-data/service-provider/manage-contents";
import { slugify } from "../../api-data/util/baseDto";
import playstoreBadgeWhite from "../../assets/playstore-badge-white.webp";
import appStoreBadge from "../../assets/app-store-badge.webp";
import OnlyOneTapLogo from "../../assets/OnlyOneTapLogo.svg";

interface DesktopFooterProps {
    footerPages: ContentPageItem[];
}

const linkSx = {
  fontSize: 14,
  color: "rgba(255,255,255,.7)",
  textDecoration: "none",
  "&:hover": { color: "#fff" },
};

const socialLinkSx = {
  width: 44,
  height: 44,
  color: linkSx.color,
  "&:hover": { ...linkSx["&:hover"], bgcolor: "rgba(255,255,255,.08)" },
  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.light", outlineOffset: 2 },
};

/** Desktop footer — 4-column layout matching DesktopChrome.jsx's DesktopFooter,
 * but only columns/links pointing at real pages: static app routes (All
 * Services, How it works, Become a Provider) plus the real CMS content pages
 * (Terms, Privacy, whatever else exists) already wired via `footerPages`.
 * Placeholder-only columns from the reference (About/Careers/Blog/Pricing/
 * Support) are dropped rather than linked to nowhere. */
const DesktopFooter: React.FC<DesktopFooterProps> = ({ footerPages }) => {
  const { t } = useTranslation();

  return (
    <Box component="footer" sx={{ bgcolor: "primary.dark", mt: 5 }}>
      <Box
        sx={{
          maxWidth: 1240,
          mx: "auto",
          px: 4,
          py: 5.5,
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr",
          gap: 5,
          boxSizing: "border-box",
        }}
      >
        {/* Brand + app download */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75 }}>
            <Box component="img" src={OnlyOneTapLogo} alt="OnlyOneTap logo" sx={{ width: 40, height: 40 }} />
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 17, letterSpacing: "1.2px" }}>
              {t("header.title")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.5, color: "rgba(255,255,255,.9)", maxWidth: 320 }}>
            {t("footer.tagline")}
          </Typography>

          <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#fff", mt: 3, mb: 1.5 }}>
            {t("mobileAppBanner.title")} {t("mobileAppBanner.subtitle")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1.5 }}>
            <a
              href="https://play.google.com/store/apps/details?id=com.indileap.onlyonetap"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Box component="img" src={playstoreBadgeWhite} alt="Get it on Google Play" sx={{ width: 140, height: 44, objectFit: "contain", borderRadius: "6px", display: "block" }} />
            </a>
            <a
              href="https://apps.apple.com/in/app/only-one-tap-home-services/id6789015962"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Box component="img" src={appStoreBadge} alt="Download on the App Store" sx={{ width: 140, height: 44, objectFit: "contain", borderRadius: "6px", display: "block" }} />
            </a>
          </Box>
        </Box>

        {/* For Customers */}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#fff", mb: 1.75 }}>
            {t("footer.forCustomers")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Link component={RouterLink} to="/all-service-categories" sx={linkSx}>
              {t("header.navAllServices")}
            </Link>
            <Link component={RouterLink} to="/how-it-works" sx={linkSx}>
              {t("header.navHowItWorks")}
            </Link>
          </Box>
        </Box>

        {/* For Providers */}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#fff", mb: 1.75 }}>
            {t("footer.forProviders")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Link component={RouterLink} to="/service-provider/register" sx={linkSx}>
              {t("header.navBecomeProvider")}
            </Link>
          </Box>
        </Box>

        {/* Resources — real CMS content pages (Terms, Privacy, etc.) */}
        {footerPages.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#fff", mb: 1.75 }}>
              {t("footer.resources")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {footerPages.map((page: ContentPageItem) => (
                <Link
                  key={page.id}
                  component={RouterLink}
                  to={`/content/${slugify(page.category)}`}
                  sx={linkSx}
                >
                  {page.localized?.category ?? page.category}
                </Link>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ borderTop: "1px solid rgba(255,255,255,.14)" }}>
        <Box
          sx={{
            maxWidth: 1240,
            mx: "auto",
            px: 4,
            py: 2.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            boxSizing: "border-box",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.6)" }}>
            {t("appInfo.copyright", { year: new Date().getFullYear() })} | {t("appInfo.productOf")}{" "}
            <a
              href="http://www.indileaps.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              {t("appInfo.companyName")}
            </a>
            {" "}| v{process.env.REACT_APP_VERSION}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: -1.25 }}>
            <IconButton
              component="a"
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("footer.facebook")}
              disableFocusRipple
              sx={socialLinkSx}
            >
              <FacebookIcon aria-hidden="true" sx={{ fontSize: 22 }} />
            </IconButton>
            <IconButton
              component="a"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("footer.instagram")}
              edge="end"
              disableFocusRipple
              sx={socialLinkSx}
            >
              <InstagramIcon aria-hidden="true" sx={{ fontSize: 22 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DesktopFooter;
